import { useState, useEffect, useCallback } from "react";

import { Pagination } from "../components/ui/pagination.js";
import { usePageBack } from "../context/PageBackContext.js";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { DatePicker } from "../components/DatePicker.js";
import { SelectField } from "../components/ui/select-field.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  resourceType: string;
  resourceId: string;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  performedAt: string;
  metadata: Record<string, unknown> | null;
  performedBy: {
    id: string; email: string;
    employee: { firstName: string; lastName: string } | null;
  };
}

interface ListMeta { total: number; page: number; limit: number; totalPages: number }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return String(p["role"] ?? "");
  } catch { return ""; }
}

import { AUDIT_ROLES as ALLOWED_ROLES } from "@hrms/types";

const RESOURCE_TYPES = [
  "Employee", "RoleFieldPermission", "TemporaryFieldGrant",
  "PendingFieldChange", "Document", "DocumentVersion",
  "OnboardingStep", "User",
];

const ACTIONS = [
  "UPDATE", "CREATE", "DELETE", "APPROVE", "REJECT",
  "GRANT", "REVOKE", "CONFIRM", "UNCONFIRM", "VERIFY",
];

const inp: React.CSSProperties = {
  padding: "7px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "13px",
  background: "var(--color-surface-0)", outline: "none",
};

const actionColor = (action: string): React.CSSProperties => {
  if (["APPROVE", "VERIFY", "CONFIRM", "CREATE", "GRANT"].includes(action))
    return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (["REJECT", "REVOKE", "DELETE"].includes(action))
    return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (["UNCONFIRM"].includes(action))
    return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};

type GroupedEntry =
  | { type: "single"; row: AuditEntry }
  | { type: "batch"; key: string; rows: AuditEntry[]; performer: AuditEntry["performedBy"]; resourceType: string; resourceId: string; action: string; timestamp: string };

function groupAuditRows(rows: AuditEntry[]): GroupedEntry[] {
  const buckets = new Map<string, AuditEntry[]>();
  for (const row of rows) {
    const minute = Math.floor(new Date(row.performedAt).getTime() / 60_000);
    const key = `${row.performedBy.id}|${row.resourceType}|${row.resourceId}|${row.action}|${minute}`;
    const g = buckets.get(key) ?? [];
    g.push(row);
    buckets.set(key, g);
  }
  const result: GroupedEntry[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const minute = Math.floor(new Date(row.performedAt).getTime() / 60_000);
    const key = `${row.performedBy.id}|${row.resourceType}|${row.resourceId}|${row.action}|${minute}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const g = buckets.get(key)!;
    if (g.length === 1) {
      result.push({ type: "single", row: g[0]! });
    } else {
      result.push({ type: "batch", key, rows: g, performer: g[0]!.performedBy, resourceType: g[0]!.resourceType, resourceId: g[0]!.resourceId, action: g[0]!.action, timestamp: g[0]!.performedAt });
    }
  }
  return result;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AuditPage() {
  usePageBack("/admin", "Back to Admin");
  const { accessToken } = useAuth();
  const role = accessToken ? decodeRole(accessToken) : "";

  const [entries, setEntries]         = useState<AuditEntry[]>([]);
  const [meta, setMeta]               = useState<ListMeta>({ total: 0, page: 1, limit: 25, totalPages: 1 });
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(25);

  // Filters
  const [filterType, setFilterType]     = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [filterFrom, setFilterFrom]     = useState("");
  const [filterTo, setFilterTo]         = useState("");
  const [filterUserId, setFilterUserId] = useState("");

  // Expanded row for metadata
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(limit) });
      if (filterType)   params.set("resourceType",  filterType);
      if (filterAction) params.set("action",         filterAction);
      if (filterFrom)   params.set("from",           filterFrom);
      if (filterTo)     params.set("to",             filterTo);
      if (filterUserId) params.set("performedById",  filterUserId);
      const { data } = await api.get<{ data: AuditEntry[]; meta: ListMeta }>(`/audit?${params}`);
      setEntries(data.data);
      setMeta(data.meta);
    } catch (err) {
      logError("AuditPage.load", err);
      setError("Failed to load audit log.");
    } finally {
      setLoading(false);
    }
  }, [filterType, filterAction, filterFrom, filterTo, filterUserId, limit]);

  useEffect(() => { void load(page); }, [page, load]);

  function applyFilters() { setPage(1); void load(1); }
  function clearFilters() {
    setFilterType(""); setFilterAction(""); setFilterFrom(""); setFilterTo(""); setFilterUserId("");
    setPage(1);
  }

  if (!ALLOWED_ROLES.has(role)) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <p style={{ color: "var(--color-surface-700)" }}>You don't have access to the audit log.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      {/* Header */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-5) var(--space-8)", display: "flex", alignItems: "center", gap: "12px",
      }}>
        <h1 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--color-surface-900)" }}>Audit Log</h1>
        <span style={{ fontSize: "12px", color: "var(--color-surface-500)" }}>{meta.total} entries</span>
      </div>

      {/* Filters */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-4) var(--space-8)", display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "3px" }}>Resource Type</div>
          <SelectField value={filterType} onChange={(e) => setFilterType(e.target.value)} specular className="w-[160px]">
            <option value="">All Types</option>
            {RESOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </SelectField>
        </div>
        <div>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "3px" }}>Action</div>
          <SelectField value={filterAction} onChange={(e) => setFilterAction(e.target.value)} specular className="w-[130px]">
            <option value="">All Actions</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </SelectField>
        </div>
        <div>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "3px" }}>From</div>
          <DatePicker value={filterFrom} onChange={setFilterFrom} style={{ ...inp, width: "130px" }} />
        </div>
        <div>
          <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--color-surface-600)", marginBottom: "3px" }}>To</div>
          <DatePicker value={filterTo} onChange={setFilterTo} style={{ ...inp, width: "130px" }} />
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
          <button onClick={applyFilters} style={{ padding: "7px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
            Apply
          </button>
          {(filterType || filterAction || filterFrom || filterTo || filterUserId) && (
            <button onClick={clearFilters} style={{ padding: "7px 12px", background: "none", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: "13px" }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: "var(--space-5) var(--space-8)" }}>
        {error && (
          <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "12px", fontSize: "13px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--color-surface-500)" }}>Loading…</div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: "center", padding: "var(--space-10)", color: "var(--color-surface-400)", background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", fontSize: "14px" }}>
            No audit entries found.
          </div>
        ) : (
          <>
            <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["When", "Performed By", "Resource", "Action", "Field", "Change", ""].map((h) => (
                      <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupAuditRows(entries).map((g) => {
                    if (g.type === "single") {
                      const e = g.row;
                      return (
                        <>
                          <tr key={e.id} style={{ borderBottom: "1px solid var(--color-surface-100)", cursor: e.metadata ? "pointer" : "default" }} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                            <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-500)", whiteSpace: "nowrap" }}>
                              {new Date(e.performedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                              {" "}
                              {new Date(e.performedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-800)" }}>
                                {e.performedBy.employee
                                  ? `${e.performedBy.employee.firstName} ${e.performedBy.employee.lastName}`
                                  : e.performedBy.email}
                              </div>
                              <div style={{ fontSize: "11px", color: "var(--color-surface-400)" }}>{e.performedBy.email}</div>
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-700)" }}>
                              <div style={{ fontWeight: 600 }}>{e.resourceType}</div>
                              <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--color-surface-400)" }}>{e.resourceId.slice(0, 8)}…</div>
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 7px", borderRadius: 999, ...actionColor(e.action) }}>
                                {e.action}
                              </span>
                            </td>
                            <td style={{ padding: "10px 12px", fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-surface-600)" }}>
                              {e.field ?? <span style={{ color: "var(--color-surface-300)" }}>—</span>}
                            </td>
                            <td style={{ padding: "10px 12px", maxWidth: "260px" }}>
                              {(e.oldValue != null || e.newValue != null) ? (
                                <div style={{ fontSize: "12px", fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                  {e.oldValue != null && (
                                    <span style={{ color: "var(--color-error)", background: "var(--color-error-bg-subtle)", padding: "1px 6px", borderRadius: 4, maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {String(e.oldValue).slice(0, 30)}
                                    </span>
                                  )}
                                  {e.oldValue != null && e.newValue != null && <span style={{ color: "var(--color-surface-400)" }}>→</span>}
                                  {e.newValue != null && (
                                    <span style={{ color: "var(--color-success)", background: "var(--color-success-bg-subtle)", padding: "1px 6px", borderRadius: 4, maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {String(e.newValue).slice(0, 30)}
                                    </span>
                                  )}
                                </div>
                              ) : <span style={{ color: "var(--color-surface-300)", fontSize: "12px" }}>—</span>}
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              {e.metadata && (
                                <span style={{ fontSize: "11px", color: "var(--color-brand-600)" }}>
                                  {expanded === e.id ? "▲" : "▼"}
                                </span>
                              )}
                            </td>
                          </tr>
                          {expanded === e.id && e.metadata && (
                            <tr key={`${e.id}-meta`} style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                              <td colSpan={7} style={{ padding: "10px 16px" }}>
                                <pre style={{ margin: 0, fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-surface-700)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {JSON.stringify(e.metadata, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    }

                    // Batch group
                    const isOpen = expandedGroups.has(g.key);
                    const toggleGroup = () => setExpandedGroups((s) => {
                      const n = new Set(s);
                      if (n.has(g.key)) n.delete(g.key); else n.add(g.key);
                      return n;
                    });
                    return (
                      <>
                        <tr key={g.key} style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)", cursor: "pointer" }} onClick={toggleGroup}>
                          <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-500)", whiteSpace: "nowrap" }}>
                            {new Date(g.timestamp).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                            {" "}
                            {new Date(g.timestamp).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-surface-800)" }}>
                              {g.performer.employee
                                ? `${g.performer.employee.firstName} ${g.performer.employee.lastName}`
                                : g.performer.email}
                            </div>
                            <div style={{ fontSize: "11px", color: "var(--color-surface-400)" }}>{g.performer.email}</div>
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: "12px", color: "var(--color-surface-700)" }}>
                            <div style={{ fontWeight: 600 }}>{g.resourceType}</div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--color-surface-400)" }}>{g.resourceId.slice(0, 8)}…</div>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, padding: "2px 7px", borderRadius: 999, ...actionColor(g.action) }}>
                              {g.action}
                            </span>
                          </td>
                          <td colSpan={2} style={{ padding: "10px 12px" }}>
                            <span style={{ fontSize: "12px", color: "var(--color-surface-500)", fontStyle: "italic" }}>
                              {g.rows.length} fields changed
                            </span>
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ fontSize: "11px", color: "var(--color-brand-600)", userSelect: "none" }}>
                              {isOpen ? "▲ hide" : "▼ show"}
                            </span>
                          </td>
                        </tr>
                        {isOpen && g.rows.map((row, ri) => (
                          <tr key={row.id} style={{ background: "#fafbfc", borderBottom: ri < g.rows.length - 1 ? "1px solid var(--color-surface-100)" : "1px solid var(--color-surface-200)" }}>
                            <td style={{ padding: "6px 12px", fontSize: "11px", color: "var(--color-surface-300)" }}>↳</td>
                            <td></td>
                            <td></td>
                            <td></td>
                            <td style={{ padding: "6px 12px", fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--color-surface-600)" }}>
                              {row.field ?? <span style={{ color: "var(--color-surface-300)" }}>—</span>}
                            </td>
                            <td style={{ padding: "6px 12px", maxWidth: "260px" }}>
                              {(row.oldValue != null || row.newValue != null) ? (
                                <div style={{ fontSize: "12px", fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                  {row.oldValue != null && (
                                    <span style={{ color: "var(--color-error)", background: "var(--color-error-bg-subtle)", padding: "1px 6px", borderRadius: 4, maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {String(row.oldValue).slice(0, 30)}
                                    </span>
                                  )}
                                  {row.oldValue != null && row.newValue != null && <span style={{ color: "var(--color-surface-400)" }}>→</span>}
                                  {row.newValue != null && (
                                    <span style={{ color: "var(--color-success)", background: "var(--color-success-bg-subtle)", padding: "1px 6px", borderRadius: 4, maxWidth: "110px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {String(row.newValue).slice(0, 30)}
                                    </span>
                                  )}
                                </div>
                              ) : <span style={{ color: "var(--color-surface-300)", fontSize: "12px" }}>—</span>}
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination meta={meta} onPageChange={setPage} onPageSizeChange={(n) => { setLimit(n); setPage(1); }} />
          </>
        )}
      </div>
    </div>
  );
}
